import { ActionTree, GetterTree, Module, MutationTree } from 'vuex'
import { IGetAllTeam, ILayout, ITeam } from '@/data/models'
import { Getters } from '@/store/getters'
import { IStoreRootState } from '@/store'
import { Actions } from '@/store/actions'
import { LayoutApiService, TeamApiService, VariableApiService } from '@/data/services'
import { IVariable } from '@/data/models/Variables'

export interface IAppModuleState {
  initialized: boolean;
  hasErrors: boolean;
  teams: Array<IGetAllTeam> | null;
  currentTeam: ITeam | null;
  loading: boolean;
  layouts: Array<ILayout> | null;
  variables: Array<IVariable> | null;
}

class AppState implements IAppModuleState {
  initialized: boolean = false
  hasErrors: boolean = false
  teams: Array<IGetAllTeam> | null = null
  currentTeam: ITeam | null = null
  loading: boolean = false
  layouts: Array<ILayout> | null = null
  variables: Array<IVariable> | null = null
}

// initial state
const state = new AppState()

// getters
const getters: GetterTree<IAppModuleState, IStoreRootState> = {
  [Getters.APP_INITIALIZED]: (state: IAppModuleState): boolean => state.initialized,
  [Getters.APP_HAS_ERRORS]: (state: IAppModuleState): boolean => state.hasErrors,
  [Getters.APP_LOADING]: (state: IAppModuleState): boolean => state.loading,
  [Getters.APP_TEAMS]: (state: IAppModuleState): Array<IGetAllTeam> | null => state.teams,
  [Getters.APP_CURRENT_TEAM]: (state: IAppModuleState): ITeam | null => state.currentTeam,
  [Getters.APP_LAYOUTS]: (state: IAppModuleState): Array<ILayout> | null => state.layouts,
  [Getters.VARIABLES_ALL]: (state: IAppModuleState): Array<IVariable> | null => state.variables,
  [Getters.VARIABLES_BY_CATEGORIES]: (state: IAppModuleState) => (categories: string[]): Array<IVariable> | null => {
    if (!state.variables) return null
    return state.variables.filter(variable => categories.includes(variable.category))
  }
}

const AppMutationsNames = {
  SET_APP_INITIALIZED: 'SET_APP_INITIALIZED',
  SET_APP_LOADING: 'SET_APP_LOADING',
  SET_APP_HAS_ERRORS: 'SET_APP_HAS_ERRORS',
  SET_APP_TEAMS: 'SET_APP_TEAMS',
  SET_APP_CURRENT_TEAM: 'SET_APP_CURRENT_TEAM',
  SET_APP_LAYOUTS: 'SET_APP_LAYOUTS',
  SET_APP_VARIABLES: 'SET_APP_VARIABLES',
  SET_APP_VARIABLES_LOADING: 'SET_APP_VARIABLES_LOADING'
}

// mutations
const mutations: MutationTree<IAppModuleState> = {
  [AppMutationsNames.SET_APP_LOADING]: function(state: IAppModuleState, value: boolean) {
    state.loading = value
  },
  [AppMutationsNames.SET_APP_INITIALIZED]: function(state: IAppModuleState, value: boolean) {
    state.initialized = value
  },
  [AppMutationsNames.SET_APP_HAS_ERRORS]: function(state: IAppModuleState, value: boolean) {
    state.hasErrors = value
  },
  [AppMutationsNames.SET_APP_TEAMS](state: IAppModuleState, value: Array<IGetAllTeam> | null) {
    state.teams = value
  },
  [AppMutationsNames.SET_APP_CURRENT_TEAM](state: IAppModuleState, value: ITeam) {
    state.currentTeam = value
  },
  [AppMutationsNames.SET_APP_LAYOUTS](state: IAppModuleState, value: Array<ILayout> | null) {
    state.layouts = value
  },
  [AppMutationsNames.SET_APP_VARIABLES](state: IAppModuleState, value: Array<IVariable> | null) {
    state.variables = value
  }
}

// actions
const actions: ActionTree<IAppModuleState, IStoreRootState> = {
  [Actions.APP_INIT]: async function({ commit, dispatch }) {
    try {
      commit(AppMutationsNames.SET_APP_LOADING, true)

      // wait till all crucial data for app fetches

      // fetch teams
      const getAllTeamsResponse = await TeamApiService.getAll()

      commit(AppMutationsNames.SET_APP_TEAMS, getAllTeamsResponse.data)

      if (getAllTeamsResponse.data && getAllTeamsResponse.data.length > 0) {
        commit(AppMutationsNames.SET_APP_CURRENT_TEAM, getAllTeamsResponse.data[0])
      }

      await dispatch(Actions.APP_FETCH_LAYOUTS)

      // Fetch all variables
      await dispatch(Actions.VARIABLES_FETCH_ALL)

      // all went without errors
      commit(AppMutationsNames.SET_APP_INITIALIZED, true)
    } catch (e) {
      // something went wrong
      commit(AppMutationsNames.SET_APP_HAS_ERRORS, true)
    } finally {
      commit(AppMutationsNames.SET_APP_LOADING, false)
    }
  },
  [Actions.APP_FETCH_TEAMS]: async function({ commit }) {
      const getAllTeamsResponse = await TeamApiService.getAll()

      commit(AppMutationsNames.SET_APP_TEAMS, getAllTeamsResponse.data)
  },
  [Actions.APP_SET_CURRENT_TEAM]: async function({ commit, dispatch }, currentTeam: ITeam | 'all') {
    commit(AppMutationsNames.SET_APP_CURRENT_TEAM, currentTeam)
    await dispatch(Actions.APP_FETCH_LAYOUTS)
  },
  [Actions.APP_DESTROY]: function({ commit }) {
    commit(AppMutationsNames.SET_APP_INITIALIZED, false)
    commit(AppMutationsNames.SET_APP_TEAMS, null)
    commit(AppMutationsNames.SET_APP_CURRENT_TEAM, null)
  },
  [Actions.APP_FETCH_LAYOUTS]: async function({ commit, state }) {
    const currentTeam: any = state.currentTeam
    const params: Record<string, unknown> = {}
    if (currentTeam && typeof currentTeam === 'object' && currentTeam.id != null) {
      params.team_id = currentTeam.id
    }
    const getAllLayoutsResponse = await LayoutApiService.getLayouts(params)

    commit(AppMutationsNames.SET_APP_LAYOUTS, getAllLayoutsResponse.data)
  },
  [Actions.VARIABLES_FETCH_ALL]: async function({ commit }) {
    const response = await VariableApiService.getVariables()

    commit(AppMutationsNames.SET_APP_VARIABLES, response.data)
  }
}

const appModule: Module<IAppModuleState, IStoreRootState> = {
  state,
  getters,
  actions,
  mutations
}

export default appModule
